/**
 * Pure helpers for the Accounts views' application (tenant) context.
 *
 * Mirrors the Payments page's module, with two deliberate differences:
 *
 * 1. the storage key is `accounts.application_id`, **distinct** from
 *    `payments.application_id`, so switching tenants in one view never silently
 *    switches the other; and
 * 2. there is **no auto-selection** — not even when the caller owns exactly one
 *    application. The Accounts views prompt instead, because every account
 *    request carries real end-user money under a tenant the operator must have
 *    chosen on purpose.
 *
 * Kept free of React and the DOM so the decisions are unit-testable without a
 * browser.
 */

export const APPLICATION_ID_HEADER = 'X-Application-ID'
export const STORAGE_KEY = 'accounts.application_id'

export interface ApplicationOption {
  id: number
  name: string
}

/** Parse a stored value; anything that is not a positive integer is "nothing stored". */
export function parseStoredApplicationId(raw: string | null | undefined): number | null {
  if (raw == null) return null
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const id = Number(trimmed)
  return id > 0 ? id : null
}

export interface RestoredSelection {
  /** The application to use, or null for the unselected (prompting) state. */
  selected: number | null
  /** True when a stored id was dropped because it is not offered any more. */
  cleared: boolean
}

/**
 * Resolve a restored selection against the application list the dashboard
 * returns for the current caller.
 *
 * A stored id absent from that list — the application was deleted, or the
 * operator's access to it changed — is cleared and the view returns to the
 * unselected state, rather than issuing a request under an id the caller was
 * not offered. A single-application list is still **not** auto-selected.
 */
export function resolveRestoredSelection(
  storedId: number | null,
  applications: readonly ApplicationOption[]
): RestoredSelection {
  if (storedId == null) return { selected: null, cleared: false }
  if (applications.some((a) => a.id === storedId)) {
    return { selected: storedId, cleared: false }
  }
  return { selected: null, cleared: true }
}

export function tenantHeaders(applicationId: number): Record<string, string> {
  return { [APPLICATION_ID_HEADER]: String(applicationId) }
}

export type ListView = 'loading' | 'error' | 'select-application' | 'empty' | 'table'

export interface ListViewInput {
  loading: boolean
  error: string | null
  selectedApplicationId: number | null
  rowCount: number
  /** True only after a successful list response for the current selection. */
  loaded: boolean
}

/**
 * Decide what the list area shows. Order matters: a failed request must never
 * be masked as "no accounts", and "no accounts" is only claimed after a
 * successful load.
 */
export function resolveListView(input: ListViewInput): ListView {
  if (input.error) return 'error'
  if (input.selectedApplicationId == null) return 'select-application'
  if (input.loading || !input.loaded) return 'loading'
  return input.rowCount === 0 ? 'empty' : 'table'
}

export function readStoredApplicationId(storage: Pick<Storage, 'getItem'> | null): number | null {
  try {
    return parseStoredApplicationId(storage?.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

export function writeStoredApplicationId(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null,
  id: number | null
): void {
  try {
    if (id == null) storage?.removeItem(STORAGE_KEY)
    else storage?.setItem(STORAGE_KEY, String(id))
  } catch {
    // Storage unavailable (private mode, quota) — selection just won't persist.
  }
}
